import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { useClubs } from '../../../hooks/clubs/useClubs'
import { usePriceRules } from '../../../hooks/pricing/usePriceRules'
import { useCourtOptions } from '../../../hooks/court/useCourtOptions'

import Icon from '../../../components/ui/Icon'
import { Tabs } from '../../../components/tabs/Tabs'
import Loading from '../../../components/loading/Loading'
import { Button } from '../../../components/buttons/Button'
import PageHeader from '../../../components/ui/pageHeader/PageHeader'
import { PriceRuleCard } from '../../../components/authenticated/management/pricing/priceRuleCard/PriceRuleCard'

import { useApp } from '../../../contexts/AppContext'
import { useConfirm } from '../../../contexts/ConfirmContext'
import { BannerKind } from '../../../types/contexts/AppContext'
import { ConfirmLayout } from '../../../types/confirm/confirm'
import { PriceRule } from '../../../types/booking/booking'
import { ClubModelMetadata } from '../../../types/court/court'
import { ActionType } from '../../../constants/actionTypes'
import { DayOfWeekOrder } from '../../../utils/dateUtils'

/** Index of the first weekday a rule covers, Monday first. */
const firstDay = (rule: PriceRule) =>
  Math.min(...rule.weekdays.map(day => DayOfWeekOrder.indexOf(day)))

/** Rules by the first weekday they cover, then by start time; ties by end time and name. */
const byWeekdayAndTime = (a: PriceRule, b: PriceRule) =>
  firstDay(a) - firstDay(b) ||
  a.interval.startTime.localeCompare(b.interval.startTime) ||
  a.interval.endTime.localeCompare(b.interval.endTime) ||
  a.name.localeCompare(b.name)

/**
 * Single-session price rules of a club. Each start time is priced by the rule whose courts, weekdays, interval and
 * effective period match; overlapping active rules are rejected by the server.
 */
export const ManagerPricing = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { showBanner } = useApp()
  const { confirm } = useConfirm()
  const { t } = useTranslation(['pricing', 'common', 'bookings'])
  const clubsHook = useClubs()
  const { list, archive } = usePriceRules()
  const { courts } = useCourtOptions()
  const [clubs, setClubs] = useState<ClubModelMetadata[] | null>(null)
  const [rules, setRules] = useState<PriceRule[] | null>(null)
  const clubId = Number(searchParams.get('clubId')) || clubs?.[0]?.id
  const club = clubs?.find(item => item.id === clubId)

  useEffect(() => {
    clubsHook.clubs(50).then(page => setClubs(page?.content ?? []))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const load = useCallback(() => {
    if (!clubId) {
      return
    }
    setRules(null)
    list(clubId)
      .then(result => setRules([...(result ?? [])].sort(byWeekdayAndTime)))
      .catch(caught => showBanner({ kind: BannerKind.ERR, msg: (caught as Error).message }))
  }, [clubId, list, showBanner])

  useEffect(() => {
    load()
  }, [load])

  const courtNames = useMemo(
    () => new Map(courts.map(court => [court.id, court.name])),
    [courts]
  )

  const handleArchive = (rule: PriceRule) =>
    confirm({
      layout: ConfirmLayout.DETAILS,
      title: t('pricing.manager.archive-title'),
      confirmLabel: t('pricing.manager.archive'),
      backLabel: t('bookings:booking-confirmation.back-label'),
      returnTo: `/m/pricing?clubId=${rule.clubId}`,
      warning: t('pricing.manager.archive-warning'),
      itemCards: [{ id: rule.id!, title: rule.name, details: [] }],
      onConfirm: async () => {
        await archive(rule.id!)
        showBanner({ kind: BannerKind.OK, msg: t('pricing.successfully-archived') })
      },
    })

  const handleAction = (rule: PriceRule, action: ActionType) => {
    switch (action) {
      case ActionType.EDIT:
        navigate(`/m/pricing/${rule.id}`)
        return
      case ActionType.DELETE:
        handleArchive(rule)
        return
    }
  }

  return (
    <>
      <PageHeader
        title={{ eyebrow: t('pricing.manager.eyebrow'), h1: t('pricing.manager.title') }}
        sub={t('pricing.manager.sub')}
        actions={[
          <Button
            key="new"
            label={t('pricing.manager.new')}
            icon={<Icon name="plus" size={14} />}
            size="md"
            disabled={!clubId}
            onClick={() => navigate(`/m/pricing/new?clubId=${clubId}`)}
          />,
        ]}
      />

      {clubs && clubs.length > 1 && (
        <div className="mb-16">
          <Tabs
            tab={String(clubId ?? '')}
            tabs={clubs.map(club => ({ value: String(club.id), label: club.name }))}
            onChange={value => setSearchParams({ clubId: value })}
          />
        </div>
      )}

      {rules === null ? (
        <Loading variant="inline" />
      ) : rules.length === 0 ? (
        <div className="empty muted">{t('pricing.manager.empty')}</div>
      ) : (
        <div className="row g-3">
          {rules.map(rule => (
            <div key={rule.rootId ?? rule.id} className="col-12">
              <PriceRuleCard
                rule={rule}
                courtNames={courtNames}
                openTime={club?.openTime}
                closeTime={club?.closeTime}
                onAction={handleAction}
              />
            </div>
          ))}
        </div>
      )}
    </>
  )
}
